import { AnswersFormated, Input, MultiSelect, Option, Question, Select, Selector } from '@hub/shared'
import { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './group-questions.component.scoped.scss'

interface Props extends HTMLAttributes<HTMLDivElement> {
  questions: Question[]
  pushFormSubmit?: () => void
  setValidate?: (validate: boolean) => void
  setData?: (data: AnswersFormated) => void
  data?: AnswersFormated
  impersonateUser?: any
  loading?: boolean
  language: string
}

export default ({
  questions = [],
  pushFormSubmit,
  impersonateUser = false,
  setValidate,
  setData,
  data = {},
  loading = false,
  language,
}: Props) => {
  const [allValid, setAllValid] = useState(false)
  const [dataValidation, setDataValidation] = useState<{
    [key: string]: boolean
  }>({})
  const [t] = useTranslation('profileProgressivePage')

  const callSetData = useCallback(
    (data: AnswersFormated) => {
      if (setData && typeof setData === 'function') {
        setData(data)
      }
    },
    [setData]
  )

  const handleChange = useCallback(
    (fieldName: string, fieldValue: any) => {
      const newData = { ...data, [fieldName]: { answer: fieldValue, options: [], id: data[fieldName]?.id } }
      callSetData(newData)
    },
    [data, callSetData]
  )

  const handleChangeToggle = useCallback(
    (fieldName: string, checked = false) => {
      const newData = { ...data, [fieldName]: { answer: checked ? 'TRUE' : 'FALSE', options: [], id: data[fieldName]?.id } }
      callSetData(newData)
    },
    [data, callSetData]
  )

  const handleChangeSelect = useCallback(
    (fieldName: string, fieldValue: any) => {
      const newData = {
        ...data,
        [fieldName]: { answer: null, options: [{ option: fieldValue, id: data[fieldName]?.id }], id: data[fieldName]?.id },
      }
      callSetData(newData)
    },
    [data, callSetData]
  )

  const handleChangeCheckbox = useCallback(
    (fieldName: string, fieldValue: any) => {
      const newData = { ...data }
      newData[fieldName] = { options: [], answer: null }
      fieldValue.forEach((value: any) => {
        newData[fieldName].options.push({
          option: value?.id,
          id: data[fieldName]?.options?.find((o: any) => o.option === fieldValue)?.id,
        })
      })

      callSetData(newData)
    },
    [data, callSetData]
  )

  const handleChangeRadio = useCallback(
    (fieldName: string, fieldValue: any, checked = false) => {
      const newData = { ...data }
      newData[fieldName] = { options: [], answer: null }
      if (checked && !newData[fieldName].options.includes(fieldValue)) {
        newData[fieldName].options.push({
          option: fieldValue,
          id: data[fieldName]?.options?.find((o: any) => o.option === fieldValue)?.id,
        })
      }

      if (!checked) {
        newData[fieldName].options = newData[fieldName].options.filter((o: any) => o.option !== fieldValue)
      }

      callSetData(newData)
    },
    [data, callSetData]
  )

  const validateAll = useCallback(
    (validation: any) => {
      const allValid = Object.keys(validation).every((key: any) => validation[key] || questions[key]?.required === 0)
      setAllValid(allValid)
      if (setValidate && typeof setValidate === 'function') {
        setValidate(allValid)
      }
    },
    [setValidate, questions]
  )

  const validate = useCallback(
    (fieldName: string, valid: boolean) => {
      const newDataValidation = { ...dataValidation, [fieldName]: valid }
      setDataValidation(newDataValidation)
      validateAll(newDataValidation)
    },
    [dataValidation, validateAll]
  )

  const submit = useCallback(
    (e: any) => {
      e.preventDefault()
      if (allValid && typeof pushFormSubmit === 'function' && (!impersonateUser || impersonateUser?.impersonateAdmin)) {
        pushFormSubmit()
      }
    },
    [pushFormSubmit, impersonateUser, allValid]
  )

  useEffect(() => {
    const newDataValidation: {
      [key: string]: boolean
    } = {}
    questions?.map((question) => {
      newDataValidation[question.question_id] =
        question.type === 'boolean' ||
        question?.required === 0 ||
        !!data[question.question_id]?.answer ||
        !!data[question.question_id]?.options?.length
    })
    setDataValidation(newDataValidation)
    validateAll(newDataValidation)
  }, [questions, validateAll, data])

  const sortFunction = (a: any, b: any) => {
    if (a.labels[language] < b.labels[language]) {
      return -1
    }
    if (a.labels[language] > b.labels[language]) {
      return 1
    }
    return 0
  }

  const parseValues = useCallback(
    (question: Question) => {
      return data?.[question?.question_id]?.options?.map((opt) => ({
        id: opt?.option,
        label: question?.options?.find((o) => opt?.option === o?.option_id)?.labels?.[language],
      }))
    },
    [data, language]
  )

  return (
    <div className="group-questions">
      <form onSubmit={(e: any) => submit(e)}>
        {questions
          .sort((a: Question, b: Question) => a.order - b.order)
          ?.map((question: Question) => {
            const label = `${question.labels[language]} ${question.required === 1 && question.type !== 'boolean' ? '*' : ''}`
            if (question.type === 'select') {
              return (
                <Select
                  label={label}
                  value={data[question.question_id]?.options[0]?.option as string}
                  name={question.question_id}
                  id={question.question_id}
                  key={question.question_id}
                  className="mb-3"
                  onChange={(value) => handleChangeSelect(question.question_id, value)}
                  onValidate={(valid) => validate(question.question_id, valid)}
                  required={question.required === 1}
                  placeholder="-"
                  readOnly={loading}
                >
                  {question?.options?.sort(sortFunction)?.map((option: Option) => (
                    <option key={option.option_id} value={option.option_id}>
                      {option.labels[language]}
                    </option>
                  ))}
                </Select>
              )
            }

            if (question.type === 'radio') {
              return (
                <div className="mb-5 wrapper-selectos-label">
                  <label className="label">{label}</label>
                  <div className="wrapper-selectors-grouped">
                    {question?.options?.sort(sortFunction)?.map((option: Option) => (
                      <Selector
                        errorSpan={false}
                        rightLabel={option.labels[language]}
                        name={question.question_id}
                        id={option.option_id}
                        type={question.type as 'radio'}
                        key={option.option_id}
                        onChange={(checked: boolean) => handleChangeRadio(question.question_id, option.option_id, checked)}
                        onValidate={(valid) => validate(question.question_id, valid)}
                        checked={!!data[question.question_id]?.options.find((o: any) => o.option === option.option_id)}
                        required={question.required === 1}
                        readOnly={loading}
                      />
                    ))}
                  </div>
                </div>
              )
            }

            if (question.type === 'checkbox') {
              return (
                <div className="mb-5 wrapper-selectos-label">
                  <label className="label">{label}</label>
                  <div className="wrapper-selectors-grouped">
                    <MultiSelect
                      errorSpan={false}
                      name={question.question_id}
                      id={question.option_id}
                      key={question.question_id}
                      options={
                        question?.options?.map((option: Option) => ({ id: option.option_id, label: option.labels[language] })) as any
                      }
                      value={parseValues(question)}
                      onChange={(answer) => handleChangeCheckbox(question.question_id, answer)}
                      required={question.required === 1}
                      readOnly={loading}
                    />
                  </div>
                </div>
              )
            }

            if (question.type === 'boolean') {
              return (
                <div className="mb-5 wrapper-selectos-label">
                  <label className="label">{label}</label>
                  <div className="wrapper-selectors-grouped">
                    {['TRUE', 'FALSE']?.map((option) => (
                      <Selector
                        rightLabel={option === 'TRUE' ? t('yes') : t('no')}
                        name={`${question.question_id}-${option}`}
                        id={`${question.question_id}-${option}`}
                        type={'radio'}
                        wrapperClass="justify-content-space-between"
                        className="mb-3"
                        onChange={() => handleChangeToggle(question.question_id, option === 'TRUE' ? true : false)}
                        checked={data[question.question_id]?.answer === option}
                        errorSpan={false}
                        readOnly={loading}
                      />
                    ))}
                  </div>
                </div>
              )
            }
            return (
              <Input
                label={label}
                type="text"
                value={data[question.question_id]?.answer}
                name={question.question_id}
                className="mb-5"
                key={question.question_id}
                id={question.question_id}
                readOnly={loading}
                onChange={(value) => handleChange(question.question_id, value)}
                onValidate={(valid) => validate(question.question_id, valid)}
                required={question.required === 1}
                maxLength={255}
              />
            )
          })}
        <p className="text-right mb-0">
          <small>* {t('required')}</small>
        </p>
      </form>
    </div>
  )
}
